import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';
import LoadingMixin from '@/mixins/LoadingMixin';
import {
    BusinessResidence,
    SelectedServiceProduct,
    Service,
    ServiceType,
    ServiceTypeId
} from '@/products-and-services/ProductsAndServicesModels';
import IconsMixin from '@/mixins/IconsMixin';
import ProductsServicesMixin from '@/mixins/ProductsServicesMixin';
import Modal from "@/components/Modal.vue";
import ProductsView from './ProductsView.vue';
import ProductMixin from '@/mixins/ProductMixin';
import BekChannelGuide
    from "../../products-and-services/bek-channel-guide-component/bek-channel-guide/bekChannelGuide.vue";
import ThemeMixin from "@/mixins/ThemeMixin";
import SectionSummary from "@/views/products-and-services/components/SectionSummary.vue";
import { SectionItem } from "@/views/products-and-services/components/SectionInterfaces";
import BusinessTvService from "@/views/products-and-services/components/BusinessTvService.vue";
import PhoneNumber from '@/components/PhoneNumber.vue';

@Component({
    components: {
        BusinessTvService,
        Modal,
        ProductsView,
        BekChannelGuide,
        SectionSummary,
        PhoneNumber
    },
})
export default class ServicesView extends Mixins(LoadingMixin, IconsMixin, ProductsServicesMixin, ProductMixin, ThemeMixin) {
    @Prop({ type: String, required: false })
    private serviceTypeId!: string;
    @Prop({ type: Boolean, required: false })
    private showCompleteView!: boolean;
    @Prop({ type: String, required: false })
    private currentSelectedServiceTypeId!: string;

    private isEditing: boolean = false;
    private showRemoveServiceModal: boolean = false;
    private storedService: SelectedServiceProduct = {} as SelectedServiceProduct;
    private setBusinessModelSize = false;

    created() {
        this.setBusinessModelSize = this.isBusinessServices;
    }

    get serviceSummaryItems(): SectionItem[] {
        const service: any = this.selectedService || {};
        return [
            { id: service.serviceId, catId: service.serviceCatId, name: service.name, amount: service.amount }
        ];
    }

    @Watch("currentSelectedServiceTypeId")
    isEditable() {
        // isEditing is responsible for knowing whether or not the user took action on editing a service and second check sees if they moved to a different service
        // if both are true then display appropriate accordion section for service
        this.isEditing = this.isEditing && this.currentSelectedServiceTypeId === this.serviceTypeId;
    }

    get serviceType(): ServiceType {
        const serviceType = this.$store.getters.getServiceTypeById(this.serviceTypeId);
        if (serviceType) {
            return serviceType;
        }

        return {} as ServiceType;
    }

    get selectedService(): Service | undefined {
        const selectedOptions = this.$store.state.shoppingCartData.selectedOptions;
        if (selectedOptions && selectedOptions.services) {
            return selectedOptions.services.find((service: Service) => service.serviceTypeId === this.serviceType.id);
        }
    }

    phoneServiceNotes(service: Service): string {
        let notes = '';
        if (this.serviceTypeId === ServiceTypeId.Telephone && this.isBusinessServices) {
            if (!service.phoneIsUnlimited) {
                notes += '<br><br>Additional lines, multi-line systems, and long distance options are available at time of sign up. Ask a BEK representative for more information.';
            } else {
                notes = '<br><br>Additional lines or multi-line systems are available at time of sign up. Ask a BEK representative for more information.';
            }
        }
        return notes;
    }

    get availableServices() {
        if (this.$store.state.productIdForCrossSaleCatalogItem && this.serviceType.services) {
            return this.serviceType.services.filter(service =>
                service.categories && service.categories.some(category => category.products.some(product => product.productId === this.$store.state.productIdForCrossSaleCatalogItem)));
        }
        return this.serviceType.services;
    }

    get showBusinessTvView(): boolean {
        const isBusiness = this.$store.state.shoppingCartData.businessResidence === BusinessResidence.Business;
        const isThisTv = this.serviceTypeId === ServiceTypeId.TV;
        const isTvSelected = this.currentSelectedServiceTypeId === ServiceTypeId.TV || (this.selectedService && this.selectedService.serviceTypeId === ServiceTypeId.TV);
        const isLocationSelected = this.$store.state.shoppingCartData.serviceAddress && this.$store.state.shoppingCartData.serviceCity;
        return isBusiness && isThisTv && isTvSelected && isLocationSelected;
    }

    private mounted() {
        this.scrollServicesIntoView();
    }

    private scrollServicesIntoView() {
        // We need to wait for animations to finish in case one accordion is expanding and another is closing.
        // Otherwise the scroll messes up and tries to scroll to the previously opened accordion        // this is needed in order to let animations finish
        return setTimeout(() => {
            document.getElementById(`services-${this.serviceTypeId}`) &&
                document.getElementById(`services-${this.serviceTypeId}`)!.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 250);
    }

    private resolveLearnMore(link: string) {
        window.open(link, "_blank");
    }

    hasSelectedService(serviceId: string, serviceCatId: string): boolean {
        if (serviceId && serviceCatId) {
            return this.isInCart({ serviceTypeId: this.serviceTypeId, serviceId, serviceCatId });
        }
        return false;
    }

    getSelectedService(serviceId: string, serviceCatId: string): SelectedServiceProduct {
        return {
            serviceTypeId: this.serviceTypeId,
            serviceId,
            serviceCatId,
        };
    }

    async addService(serviceId: string, serviceCatId: string) {
        // this will check if user already has a selected service and if so then show modal.
        if (this.selectedService) {
            this.storedService = this.getSelectedService(serviceId, serviceCatId);
            this.showRemoveServiceModal = true;
        } else {
            await this.addToCart(this.getSelectedService(serviceId, serviceCatId));
            this.isEditing = false;
            this.clearProductIdForCrossSaleCatalogItem();
        }
    }

    async addStoredService() {
        await this.removeFromCart(this.getSelectedService(this.selectedService!.serviceId, this.selectedService!.catId!));
        await this.addToCart(this.storedService);
        this.storedService = {} as SelectedServiceProduct;
        this.showRemoveServiceModal = false;
        this.isEditing = false;
        this.clearProductIdForCrossSaleCatalogItem();
    }

    private editClicked() {
        this.isEditing = true;
        this.$emit("edit-clicked", this.serviceTypeId);
    }

    private editProductClicked(value: string) {
        //reset isEditing since product is being edited and not service
        this.isEditing = false;
        this.$emit("edit-clicked", value);
    }

    private clearProductIdForCrossSaleCatalogItem() {
        this.$store.commit("setProductIdForCrossSaleCatalogItem", '');
    }

    beforeDestroy() {
        this.clearProductIdForCrossSaleCatalogItem()
    }

    private productsComplete() {
        this.$emit("products-complete");
    }
}
